import {useState} from 'react';

type StepIndicatorProps = {
  steps: {label: string}[];
  currentStep: number;
  maxStepReached: number;
  ariaLabel?: string;
  onStepClick: (step: number) => void;
  mobileDropdown?: boolean;
  withBackdrop?: boolean;
};

function CheckmarkIcon() {
  return (
    <svg width="12" height="10" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fillRule="evenodd" clipRule="evenodd" d="M13.6249 0.220523C13.7898 0.383628 13.7898 0.649918 13.6249 0.813023L4.41042 9.93048L0.223719 5.78787C0.0588786 5.62477 0.0588788 5.35848 0.223719 5.19538C0.386109 5.0347 0.647587 5.0347 0.809978 5.19538L4.41042 8.7579L13.0387 0.220524C13.201 0.0598431 13.4625 0.059843 13.6249 0.220523Z" fill="currentColor" stroke="currentColor" strokeWidth="1"/>
    </svg>
  );
}

export function StepIndicator({steps, currentStep, maxStepReached, ariaLabel, onStepClick, mobileDropdown, withBackdrop}: StepIndicatorProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const progressPct = steps.length > 1
    ? ((currentStep - 1) / (steps.length - 1)) * 100 * ((steps.length - 1) / steps.length) + (100 / steps.length / 2)
    : 50;

  const currentLabel = steps[currentStep - 1]?.label ?? '';

  return (
    <>
      {/* Backdrop rendered OUTSIDE stepper-mobile so stepper's z-index sits above it */}
      {mobileDropdown && withBackdrop && mobileOpen ? (
        <div className="bsb__stepper-mobile-backdrop" aria-hidden="true" onClick={() => setMobileOpen(false)} />
      ) : null}

      {mobileDropdown ? (
        <div className={`bsb__stepper-mobile${mobileOpen ? ' is-open' : ''}`} aria-label={ariaLabel ?? 'Builder steps'}>
          <div className="bsb__stepper-mobile-track">
            <div className="bsb__stepper-mobile-progress" style={{width: `${progressPct}%`}} />
          </div>
          <button
            type="button"
            className="bsb__stepper-mobile-trigger"
            onClick={() => setMobileOpen(prev => !prev)}
            aria-expanded={mobileOpen}
          >
            <span className="bsb__stepper-circle bsb__stepper-circle--current" aria-hidden="true">
              {currentStep}
            </span>
            {currentLabel ? <span className="bsb__stepper-mobile-label">{currentLabel}</span> : null}
            <span className="bsb__stepper-mobile-count">step {currentStep}/{steps.length}</span>
            <svg
              className={`bsb__stepper-mobile-chevron${mobileOpen ? ' is-open' : ''}`}
              width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {mobileOpen ? (
            <ul className="bsb__stepper-mobile-panel" role="list">
              {steps.map((s, i) => {
                const stepNum = i + 1;
                const isComplete = currentStep > stepNum;
                const isCurrent = currentStep === stepNum;
                const wasCompleted = maxStepReached > stepNum;
                const isReachable = maxStepReached >= stepNum;
                const showCheck = isComplete;
                if (stepNum === 1 && isCurrent) return null;
                return (
                  <li key={stepNum} role="listitem">
                    <button
                      type="button"
                      className={`bsb__stepper-mobile-item${isCurrent ? (wasCompleted ? ' is-current-complete' : ' is-current') : isComplete ? ' is-complete' : ''}`}
                      disabled={!isReachable}
                      aria-disabled={!isReachable}
                      aria-current={isCurrent ? 'step' : undefined}
                      onClick={() => {
                        if (!isReachable) return;
                        onStepClick(stepNum);
                        setMobileOpen(false);
                      }}
                    >
                      <span className="bsb__stepper-circle" aria-hidden="true">
                        {showCheck ? <CheckmarkIcon /> : stepNum}
                      </span>
                      {s.label ? <span className="bsb__stepper-mobile-item-label">{s.label}</span> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}

      <nav
        className={`bsb__stepper${mobileDropdown ? ' bsb__stepper--has-mobile-dropdown' : ''}`}
        aria-label={ariaLabel ?? 'Builder steps'}
        style={{['--bsb-progress' as any]: `${progressPct}%`}}
      >
        <ol className="bsb__stepper-list" style={{gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`}}>
          {steps.map((s, i) => {
            const stepNum = i + 1;
            const isCurrent = currentStep === stepNum;
            const isComplete = currentStep > stepNum;
            const isReachable = maxStepReached >= stepNum;
            return (
              <li
                key={stepNum}
                className={`bsb__stepper-item${isCurrent ? ' is-current' : isComplete ? ' is-complete' : ''}`}
              >
                <button
                  type="button"
                  className="bsb__stepper-btn"
                  onClick={() => isReachable && onStepClick(stepNum)}
                  disabled={!isReachable}
                  aria-disabled={!isReachable}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  <span className="bsb__stepper-circle" aria-hidden="true">{stepNum}</span>
                  {s.label ? <span className="bsb__stepper-label">{s.label}</span> : null}
                </button>
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
